/**
 * ModuleControls - Solar module configuration and placement controls
 * Displayed in System mode under Roof section
 */

import React from 'react';
import { Square, Trash2, Plus, Settings, Edit3 } from 'lucide-react';
import { AuroraCard, AuroraCardSection, AuroraButton } from './AuroraCard';

interface ModuleControlsProps {
  // Polygon context
  selectedPolygon: number | null;
  polygonData: any;

  // Segment selection
  selectedSegment: { polygonId: number; index: number } | null;
  moduleSegments: any;

  // Panel selection
  availableBrands: string[];
  selectedBrand: string;
  onBrandChange: (brand: string) => void;
  availablePanels: any[];
  selectedPanel: any;
  onPanelChange: (model: string) => void;
  panelsLoading: boolean;

  // Module configuration
  moduleWidth: number;
  moduleHeight: number;
  onWidthChange: (width: number) => void;
  onHeightChange: (height: number) => void;
  moduleOrientation: 'portrait' | 'landscape';
  onOrientationChange: (orientation: 'portrait' | 'landscape') => void;
  modulePowerWatts: number;
  onPowerChange: (power: number) => void;

  // Module count
  moduleCount: number;
  totalModuleArea: number;
  roofCoverage: number;
  systemCapacity: number;

  // Advanced controls
  moduleSpacing: number;
  onModuleSpacingChange: (spacing: number) => void;
  rowSpacingMultiplier: number;
  onRowSpacingChange: (spacing: number) => void;
  edgeSetback: number;
  onEdgeSetbackChange: (setback: number) => void;
  obstructionSetback: number;
  onObstructionSetbackChange: (setback: number) => void;
  targetModuleCount?: number | null;
  onTargetModuleCountChange?: (count: number | null) => void;
  targetFillPercentage?: number;
  onTargetFillPercentageChange?: (percentage: number) => void;

  // Actions
  onFillRoof: () => void;
  onClearModules: () => void;
  onDrawSegment?: () => void;
  onCopySegment?: () => void;

  // Free-field mode (draw segments without selecting a roof polygon)
  isFreeFieldMode?: boolean;
  onFreeFieldModeChange?: (enabled: boolean) => void;
  freeFieldAzimuth?: number;
  onFreeFieldAzimuthChange?: (azimuth: number) => void;

  // Auto-roof mode (when false, hide Fill Roof button)
  isAutoRoofMode?: boolean;

  // Segment config updates
  onSegmentSpacingChange?: (moduleSpacing: number, rowSpacing: number) => void;
  onRefillSegment?: () => void;

  // Collapse state
  collapsed?: boolean;
  onToggle?: () => void;
}

export const ModuleControls: React.FC<ModuleControlsProps> = ({
  selectedPolygon,
  polygonData,
  selectedSegment,
  moduleSegments,
  availableBrands,
  selectedBrand,
  onBrandChange,
  availablePanels,
  selectedPanel,
  onPanelChange,
  panelsLoading,
  moduleWidth,
  moduleHeight,
  onWidthChange,
  onHeightChange,
  moduleOrientation,
  onOrientationChange,
  modulePowerWatts,
  onPowerChange,
  moduleCount,
  totalModuleArea,
  roofCoverage,
  systemCapacity,
  moduleSpacing,
  onModuleSpacingChange,
  rowSpacingMultiplier,
  onRowSpacingChange,
  edgeSetback,
  onEdgeSetbackChange,
  obstructionSetback,
  onObstructionSetbackChange,
  targetModuleCount,
  onTargetModuleCountChange,
  targetFillPercentage,
  onTargetFillPercentageChange,
  onFillRoof,
  onClearModules,
  onDrawSegment,
  onCopySegment,
  isFreeFieldMode = false,
  onFreeFieldModeChange,
  freeFieldAzimuth = 180,
  onFreeFieldAzimuthChange,
  isAutoRoofMode = true,
  onSegmentSpacingChange,
  onRefillSegment,
  collapsed = false,
  onToggle,
}) => {
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  const [panelSectionOpen, setPanelSectionOpen] = React.useState(false);

  // Get current segment config if one is selected
  // Works for both roof polygon segments AND free-field segments (polygonId = -1)
  const currentSegment = selectedSegment && moduleSegments[selectedSegment.polygonId]
    ? moduleSegments[selectedSegment.polygonId][selectedSegment.index]
    : null;

  return (
    <AuroraCard
      title="Solar Modules"
      collapsed={collapsed}
      onToggle={onToggle}
    >
      <AuroraCardSection>
        {selectedPolygon === null && !isFreeFieldMode ? (
          <div className="text-sm text-gray-600 italic bg-gray-50 rounded-lg p-3 border-2 border-gray-200">
            Select a polygon first to add solar modules
          </div>
        ) : (
          <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto pr-2">
            {/* Panel Selection - Collapsible */}
            <div className="space-y-2 border-2 border-gray-200 rounded-lg p-3">
              <button
                onClick={() => setPanelSectionOpen(!panelSectionOpen)}
                className="w-full flex items-center justify-between hover:bg-gray-50 px-1 py-1 rounded transition-colors"
              >
                <span className="text-xs font-semibold text-gray-700">Solar Panel Selection</span>
                <span className="text-gray-500 text-xs">{panelSectionOpen ? '▲' : '▼'}</span>
              </button>

              {panelSectionOpen && (
              <div className="space-y-2 pt-2">
              {/* Brand Dropdown */}
              <select
                value={selectedBrand}
                onChange={(e) => onBrandChange(e.target.value)}
                className="w-full text-xs px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-[#EBFD03] focus:outline-none transition-colors"
              >
                {availableBrands.length === 0 && (
                  <option value="">Loading brands...</option>
                )}
                {availableBrands.map((brand: string) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>

              {/* Model Dropdown */}
              {panelsLoading ? (
                <div className="text-xs text-gray-500 italic">Loading panels...</div>
              ) : (
                <select
                  value={selectedPanel?.model || ''}
                  onChange={(e) => onPanelChange(e.target.value)}
                  className="w-full text-xs px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-[#EBFD03] focus:outline-none transition-colors"
                  disabled={availablePanels.length === 0}
                >
                  {availablePanels.length === 0 && (
                    <option value="">No panels available</option>
                  )}
                  {availablePanels.map((panel: any) => (
                    <option key={panel.model} value={panel.model}>
                      {panel.model} - {panel.power_watts}W
                    </option>
                  ))}
                </select>
              )}

              {/* Panel Info Display */}
              {selectedPanel && (
                <div className="bg-[#EBFD03]/10 border-2 border-[#EBFD03] rounded-lg p-3 text-xs space-y-1">
                  <div className="font-bold text-black">
                    {selectedPanel.brand} {selectedPanel.model}
                  </div>
                  <div className="text-gray-700 space-y-0.5">
                    <div>⚡ Power: <span className="font-semibold">{selectedPanel.power_watts}W</span></div>
                    <div>📐 Size: {(parseFloat(selectedPanel.width_mm) / 1000).toFixed(3)}m × {(parseFloat(selectedPanel.height_mm) / 1000).toFixed(3)}m</div>
                    <div>📏 Area: {selectedPanel.area_m2}m²</div>
                    {selectedPanel.efficiency && (
                      <div>✨ Efficiency: {(parseFloat(selectedPanel.efficiency) * 100).toFixed(1)}%</div>
                    )}
                    {selectedPanel.cell_type && (
                      <div>🔷 Type: {selectedPanel.cell_type}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Custom Dimensions Toggle */}
              {selectedPanel && (
                <button
                  onClick={() => onPanelChange('')}
                  className="text-xs text-gray-600 hover:text-black transition-colors underline"
                >
                  ✏️ Use custom dimensions
                </button>
              )}
              </div>
              )}
            </div>

            {/* Manual Dimensions (only if no panel selected) */}
            {!selectedPanel && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700">Dimensions (meters)</label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <input
                      type="number"
                      step="0.1"
                      min="0.5"
                      max="3"
                      value={moduleWidth}
                      onChange={(e) => onWidthChange(parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-xs focus:border-[#EBFD03] focus:outline-none"
                      placeholder="Width"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      step="0.1"
                      min="0.5"
                      max="3"
                      value={moduleHeight}
                      onChange={(e) => onHeightChange(parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-xs focus:border-[#EBFD03] focus:outline-none"
                      placeholder="Height"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Orientation */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-700">Orientation</label>
              <div className="grid grid-cols-2 gap-2">
                <AuroraButton
                  variant={moduleOrientation === 'portrait' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => onOrientationChange('portrait')}
                  className="flex items-center justify-center gap-1"
                >
                  <Square className="h-3 w-3 transform rotate-0" />
                  <span>Portrait</span>
                </AuroraButton>
                <AuroraButton
                  variant={moduleOrientation === 'landscape' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => onOrientationChange('landscape')}
                  className="flex items-center justify-center gap-1"
                >
                  <Square className="h-3 w-3 transform rotate-90" />
                  <span>Landscape</span>
                </AuroraButton>
              </div>
            </div>

            {/* Advanced Controls - Hidden in free-field/manual mode (use segment controls instead) */}
            {!isFreeFieldMode && (
            <div className="border-t-2 border-gray-200 pt-3">
              <button
                onClick={() => setAdvancedOpen(!advancedOpen)}
                className="w-full flex items-center justify-between mb-2 hover:bg-gray-50 px-2 py-1 rounded transition-colors"
              >
                <span className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Advanced Controls
                </span>
                <span className="text-gray-500 text-xs">{advancedOpen ? '▲' : '▼'}</span>
              </button>

              {advancedOpen && (
                <div className="space-y-3">
                  {/* Module Spacing */}
                  <div>
                    <label className="text-xs font-semibold text-gray-700 block mb-1">
                      Module Spacing: {(moduleSpacing * 100).toFixed(0)}cm
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="0.2"
                      step="0.01"
                      value={moduleSpacing}
                      onChange={(e) => onModuleSpacingChange(parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  {/* Row Spacing */}
                  <div>
                    <label className="text-xs font-semibold text-gray-700 block mb-1">
                      Row Spacing: {rowSpacingMultiplier.toFixed(1)}x
                    </label>
                    <input
                      type="range"
                      min="1.0"
                      max="2.0"
                      step="0.1"
                      value={rowSpacingMultiplier}
                      onChange={(e) => onRowSpacingChange(parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  {/* Edge Setback */}
                  <div>
                    <label className="text-xs font-semibold text-gray-700 block mb-1">
                      Edge Setback: {(edgeSetback * 100).toFixed(0)}cm
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="0.5"
                      step="0.05"
                      value={edgeSetback}
                      onChange={(e) => onEdgeSetbackChange(parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  {/* Obstruction Setback */}
                  <div>
                    <label className="text-xs font-semibold text-gray-700 block mb-1">
                      Obstruction Setback: {(obstructionSetback * 100).toFixed(0)}cm
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1.0"
                      step="0.05"
                      value={obstructionSetback}
                      onChange={(e) => onObstructionSetbackChange(parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  {/* Target Fill Percentage */}
                  {onTargetFillPercentageChange && (
                    <div>
                      <label className="text-xs font-semibold text-gray-700 block mb-1">
                        Target Fill: {targetFillPercentage}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        value={targetFillPercentage || 50}
                        onChange={(e) => onTargetFillPercentageChange(parseFloat(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  )}

                  {/* Target Module Count */}
                  {onTargetModuleCountChange && (
                    <div>
                      <label className="text-xs font-semibold text-gray-700 block mb-1">
                        Target Module Count
                      </label>
                      <input
                        type="number"
                        min="0"
                        placeholder="Auto (no limit)"
                        value={targetModuleCount || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || val === '0') {
                            onTargetModuleCountChange(null);
                          } else {
                            onTargetModuleCountChange(parseInt(val));
                          }
                        }}
                        className="w-full text-xs px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-[#EBFD03] focus:outline-none transition-colors"
                      />
                      <div className="text-xs text-gray-500 mt-1 italic">
                        Leave empty for automatic mode
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Per-Segment Adjustments - Only show in roof mode (SystemModePanel handles free-field) */}
            {!isFreeFieldMode && currentSegment && onSegmentSpacingChange && (
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3 space-y-3">
                <div className="text-xs font-bold text-blue-900">
                  Segment {currentSegment.id} Config
                </div>

                {/* Segment Module Spacing */}
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">
                    Module Spacing: {(currentSegment.config.moduleSpacing * 100).toFixed(0)}cm
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="0.2"
                    step="0.01"
                    value={currentSegment.config.moduleSpacing}
                    onChange={(e) => {
                      const newModuleSpacing = parseFloat(e.target.value);
                      onSegmentSpacingChange(newModuleSpacing, currentSegment.config.rowSpacing);
                    }}
                    onMouseUp={() => onRefillSegment?.()}
                    className="w-full"
                  />
                </div>

                {/* Segment Row Spacing */}
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">
                    Row Spacing: {currentSegment.config.rowSpacing.toFixed(1)}x
                  </label>
                  <input
                    type="range"
                    min="1.0"
                    max="2.0"
                    step="0.1"
                    value={currentSegment.config.rowSpacing}
                    onChange={(e) => {
                      const newRowSpacing = parseFloat(e.target.value);
                      onSegmentSpacingChange(currentSegment.config.moduleSpacing, newRowSpacing);
                    }}
                    onMouseUp={() => onRefillSegment?.()}
                    className="w-full"
                  />
                </div>

                {/* Copy Segment Button */}
                {onCopySegment && (
                  <AuroraButton
                    variant="secondary"
                    size="sm"
                    onClick={onCopySegment}
                    className="w-full"
                  >
                    📋 Copy Segment
                  </AuroraButton>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2 border-t-2 border-gray-200 pt-3">
              {/* Draw Segment - Only in roof mode (SystemModePanel handles free-field) */}
              {!isFreeFieldMode && onDrawSegment && (
                <AuroraButton
                  variant="secondary"
                  size="md"
                  onClick={onDrawSegment}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Edit3 className="h-4 w-4" />
                  <span>Draw Module Segment</span>
                </AuroraButton>
              )}

              {/* Only show Fill Roof button in Auto-Roof mode */}
              {isAutoRoofMode && (
                <AuroraButton
                  variant="primary"
                  size="md"
                  onClick={onFillRoof}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Fill Roof with Modules</span>
                </AuroraButton>
              )}

              {moduleCount > 0 && (
                <AuroraButton
                  variant="danger"
                  size="md"
                  onClick={onClearModules}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Clear All Modules</span>
                </AuroraButton>
              )}
            </div>
          </div>
        )}
      </AuroraCardSection>
    </AuroraCard>
  );
};
